import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { ErroDaApi } from "./cliente";
import { LICENCA_DESLIGADA, consultarLicenca, liberacaoLocal, type Liberacao } from "./licenca";
import { mensagemDoErro, useSessao } from "./sessao";

/**
 * A licença do jogo, para as telas.
 *
 * Mora acima da tela inicial e abaixo da sessão: ao abrir, confere o bilhete
 * guardado (vale sem internet); quando a sessão conecta, ou quando os direitos
 * da conta mudam (uma compra acabou de cair pelo socket), pergunta de novo ao
 * servidor. Antes de abrir uma carreira, `garantir()` pede a licença com o
 * teste, que é o que começa os sete dias na primeira vez.
 */

export interface EstadoDaLicenca {
  /** Já conferiu o bilhete guardado. Antes disso a tela não decide nada. */
  pronta: boolean;
  liberacao: Liberacao;
  /** Frase do último pedido recusado (e-mail sem confirmar, aparelhos demais). */
  erro: string | null;
  /** Pode abrir a carreira? Começa o teste se ele ainda não começou. */
  garantir: () => Promise<boolean>;
}

const SEM_LICENCA: Liberacao = { liberado: false, status: null, trialEndsAt: null, validoAte: null };

const Contexto = createContext<EstadoDaLicenca | null>(null);

export function ProvedorDeLicenca({ children }: { children: ReactNode }): ReactNode {
  const sessao = useSessao();
  const [pronta, definirPronta] = useState(false);
  const [liberacao, definirLiberacao] = useState<Liberacao>(SEM_LICENCA);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void liberacaoLocal().then((local) => {
      if (!vivo) return;
      definirLiberacao(local);
      definirPronta(true);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const conectada = sessao.estado === "conectada";
  const quem = sessao.conta?.usuario.id ?? null;
  const direitos = sessao.conta?.direitos;
  const comprou = direitos ? `${String(direitos.lifetime)}:${String(direitos.pass?.active === true)}` : "";

  useEffect(() => {
    if (!conectada || LICENCA_DESLIGADA) return;
    let vivo = true;
    consultarLicenca(sessao.cliente)
      .then(({ liberacao: nova }) => {
        if (vivo) definirLiberacao(nova);
      })
      // Sem rede, vale o bilhete guardado; qualquer outra recusa aparece
      // quando a pessoa tentar jogar, que é quando ela importa.
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [conectada, quem, comprou, sessao.cliente]);

  const garantir = useCallback(async (): Promise<boolean> => {
    definirErro(null);
    if (LICENCA_DESLIGADA) return true;
    if (sessao.estado === "conectada") {
      try {
        const { liberacao: nova } = await consultarLicenca(sessao.cliente, { iniciarTeste: true });
        definirLiberacao(nova);
        return nova.liberado;
      } catch (causa) {
        if (!(causa instanceof ErroDaApi && causa.code === "REDE")) {
          definirErro(mensagemDoErro(causa));
          return false;
        }
      }
    }
    const local = await liberacaoLocal();
    definirLiberacao(local);
    return local.liberado;
  }, [sessao.estado, sessao.cliente]);

  const valor = useMemo(() => ({ pronta, liberacao, erro, garantir }), [pronta, liberacao, erro, garantir]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useLicenca(): EstadoDaLicenca {
  const estado = useContext(Contexto);
  if (!estado) throw new Error("useLicenca precisa do ProvedorDeLicenca acima na árvore.");
  return estado;
}

/** Dias de teste que faltam, contando o dia de hoje. */
export function diasDeTeste(trialEndsAt: string | null, agora = Date.now()): number {
  if (!trialEndsAt) return 0;
  return Math.max(0, Math.ceil((Date.parse(trialEndsAt) - agora) / (24 * 60 * 60 * 1000)));
}
